const { useEffect: useEffectAD, useMemo: useMemoAD, useState: useStateAD, useRef: useRefAD } = React;

function analyticsNumber(value) {
  return Number(value || 0).toLocaleString();
}

function AnalyticsBars({ rows, valueKey = "events", empty = "No activity in this range.", showVisitors = false }) {
  const max = Math.max(1, ...(rows || []).map((row) => Number(row[valueKey]) || 0));
  if (!rows || !rows.length) return <p className="analytics-empty">{empty}</p>;
  return (
    <div className="analytics-bars">
      {rows.map((row, index) => (
        <div className="analytics-bar-row" key={`${row.label || row.day}-${index}`}>
          <span className="analytics-bar-label" title={row.label || row.day}>{row.label || row.day || "Unknown"}</span>
          <span className="analytics-bar-track"><i style={{ width: `${Math.max(0, (Number(row[valueKey]) || 0) / max * 100)}%` }} /></span>
          <b>{analyticsNumber(row[valueKey])}{showVisitors && <small className="analytics-browser-count">{analyticsNumber(row.visitors)} browsers</small>}</b>
        </div>
      ))}
    </div>
  );
}

function AnalyticsEventDetail({ event, onClose }) {
  const closeButton = useRefAD(null);
  useEffectAD(() => {
    if (!event) return;
    const previous = document.activeElement;
    closeButton.current?.focus();
    const escape = key => { if (key.key === "Escape") onClose(); };
    document.addEventListener("keydown", escape);
    return () => { document.removeEventListener("keydown", escape); previous?.focus?.(); };
  }, [event]);
  if (!event) return null;
  const sections = [
    ["Event", event.event_name],
    ["Occurred", event.occurred_at ? new Date(Number(event.occurred_at)).toLocaleString() : ""],
    ["Route", event.route],
    ["Path", event.path],
    ["Book", event.book_ref],
    ["Collection", event.collection],
    ["Search text", event.search_text],
    ["IP address", event.ip_address],
    ["Location", [event.city, event.region, event.country].filter(Boolean).join(", ")],
    ["Visitor ID", event.visitor_id],
    ["Session ID", event.session_id],
    ["Account ID", event.account_id],
    ["Account name", event.account_name],
    ["Account email", event.account_email],
    ["User agent", event.user_agent],
  ].filter(([, value]) => value !== null && value !== undefined && value !== "");
  const pretty = (value) => {
    if (!value) return "";
    try { return JSON.stringify(typeof value === "string" ? JSON.parse(value) : value, null, 2); } catch { return String(value); }
  };
  return (
    <div className="analytics-detail-scrim" onMouseDown={onClose}>
      <section className="analytics-detail" role="dialog" aria-modal="true" aria-label="Raw analytics event" onMouseDown={(event_) => event_.stopPropagation()}>
        <header><div><span>Raw event</span><h2>{event.event_name}</h2></div><button ref={closeButton} className="btn" onClick={onClose} aria-label="Close event details">Close</button></header>
        <dl>{sections.map(([label, value]) => <React.Fragment key={label}><dt>{label}</dt><dd>{String(value)}</dd></React.Fragment>)}</dl>
        <h3>Properties</h3>
        <pre>{pretty(event.properties) || "{}"}</pre>
        {event.workspace_snapshot && <><h3>Workspace snapshot, including notes</h3><pre>{pretty(event.workspace_snapshot)}</pre></>}
      </section>
    </div>
  );
}

function SiteAppearanceSettings() {
  const [visible, setVisible] = useStateAD(false);
  const [topics, setTopics] = useStateAD(false);
  const [ready, setReady] = useStateAD(false);
  const [saving, setSaving] = useStateAD(false);
  const [message, setMessage] = useStateAD("");
  useEffectAD(() => {
    fetch("/bff/site-settings", { cache: "no-store" }).then(r => { if (!r.ok) throw Error(); return r.json(); }).then(data => { setVisible(data.footerVisible); setTopics(data.topicsVisible === true); setReady(true); }).catch(() => setMessage("Could not load website settings. Reload to try again."));
  }, []);
  const save = async (key, next) => {
    setSaving(true); setMessage("");
    try {
      const response = await fetch("/bff/analytics/admin", { method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ [key]: next }) });
      if (!response.ok) throw Error();
      const settings = await response.json();
      setVisible(settings.footerVisible); setTopics(settings.topicsVisible);
      window.dispatchEvent(new CustomEvent("mayanos-site-settings", { detail: settings }));
      setMessage("Saved for all visitors.");
    } catch { setMessage("Could not save. Please try again."); }
    finally { setSaving(false); }
  };
  return <article className="analytics-panel site-appearance-settings"><header><div><span>Website appearance</span><h2>Information footer</h2></div></header><label className="site-footer-toggle"><input type="checkbox" role="switch" checked={visible} disabled={!ready || saving} onChange={event => save("footerVisible", event.target.checked)} /> Show the information footer</label><p>Display the About, Contact, and other information links at the bottom of the website.</p><h2>Topics</h2><label className="site-footer-toggle"><input type="checkbox" role="switch" checked={topics} disabled={!ready || saving} onChange={event => save("topicsVisible", event.target.checked)} /> Show Topics publicly</label><p>Enable Topics pages, navigation, and discovery sections for all visitors.</p><p role="status">{saving ? "Saving…" : message}</p></article>;
}

function AnalyticsAdminPage() {
  const [days, setDays] = useStateAD(30);
  const [summary, setSummary] = useStateAD(null);
  const [events, setEvents] = useStateAD([]);
  const [query, setQuery] = useStateAD("");
  const [eventFilter, setEventFilter] = useStateAD("");
  const [collectionFilter, setCollectionFilter] = useStateAD("");
  const [selected, setSelected] = useStateAD(null);
  const [revision, setRevision] = useStateAD(0);
  const [eventState, setEventState] = useStateAD({ loading: true, error: "", nextCursor: null });
  const [detailState, setDetailState] = useStateAD({ loading: false, error: "" });
  const eventsRequest = useRefAD(null);
  const summaryRequest = useRefAD(null);
  const detailRequest = useRefAD(null);
  const [state, setState] = useStateAD({ loading: true, error: "", signIn: "" });

  const loadSummary = async () => {
    summaryRequest.current?.abort();
    const controller = new AbortController();
    summaryRequest.current = controller;
    setState({ loading: true, error: "", signIn: "" });
    try {
      const response = await fetch(`/bff/analytics/admin?days=${days}`, { cache: "no-store", signal: controller.signal });
      const data = await response.json();
      if (!response.ok) throw Object.assign(new Error(data.error || "Analytics unavailable"), { signIn: data.signIn || "" });
      if (controller.signal.aborted) return;
      setSummary(data);
      setState({ loading: false, error: "", signIn: "" });
    } catch (error) {
      if (controller.signal.aborted) return;
      setState({ loading: false, error: error.message || "Analytics unavailable", signIn: error.signIn || "" });
    }
  };

  const loadEvents = async (cursor = null) => {
    eventsRequest.current?.abort();
    const controller = new AbortController();
    eventsRequest.current = controller;
    setEventState(previous => ({ ...previous, loading: true, error: "" }));
    const params = new URLSearchParams({ view: "events", days: String(Math.min(days, 90)) });
    if (query.trim()) params.set("q", query.trim());
    if (eventFilter) params.set("event", eventFilter);
    if (collectionFilter) params.set("collection", collectionFilter);
    if (cursor) params.set("cursor", cursor);
    if (summary?.range?.asOf) params.set("until", String(summary.range.asOf));
    try {
      const response = await fetch(`/bff/analytics/admin?${params}`, { cache: "no-store", signal: controller.signal });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || "Could not load raw events.");
      if (controller.signal.aborted) return;
      setEvents(previous => cursor ? [...previous, ...(data.events || [])] : data.events || []);
      setEventState({ loading: false, error: "", nextCursor: data.nextCursor || null });
    } catch (error) {
      if (!controller.signal.aborted) setEventState(previous => ({ ...previous, loading: false, error: error.message || "Could not load raw events." }));
    }
  };

  useEffectAD(() => { loadSummary(); return () => summaryRequest.current?.abort(); }, [days, revision]);
  useEffectAD(() => {
    eventsRequest.current?.abort();
    setEvents([]);
    setEventState({ loading: true, error: "", nextCursor: null });
    const timer = setTimeout(() => loadEvents(), 250);
    return () => { clearTimeout(timer); eventsRequest.current?.abort(); };
  }, [days, query, eventFilter, collectionFilter, revision, summary?.range?.asOf]);
  useEffectAD(() => () => detailRequest.current?.abort(), []);

  const openEvent = async (id) => {
    detailRequest.current?.abort();
    const controller = new AbortController();
    detailRequest.current = controller;
    setDetailState({ loading: true, error: "" });
    try {
      const response = await fetch(`/bff/analytics/admin?view=event&id=${encodeURIComponent(id)}`, { cache: "no-store", signal: controller.signal });
      const data = await response.json();
      if (!response.ok || !data.event) throw new Error(data.error || "Could not open event details.");
      if (!controller.signal.aborted) { setSelected(data.event); setDetailState({ loading: false, error: "" }); }
    } catch (error) {
      if (!controller.signal.aborted) setDetailState({ loading: false, error: error.message || "Could not open event details." });
    }
  };

  const eventNames = useMemoAD(() => (summary?.events || []).map((row) => row.label).filter(Boolean), [summary]);
  const exportParams = new URLSearchParams({ view: "export", days: String(Math.min(days, 90)) });
  if (summary?.range?.asOf) exportParams.set("until", String(summary.range.asOf));
  if (query.trim()) exportParams.set("q", query.trim());
  if (eventFilter) exportParams.set("event", eventFilter);
  if (collectionFilter) exportParams.set("collection", collectionFilter);

  if (state.loading && !summary) return <section className="analytics-admin analytics-admin-state"><span className="account-spinner" /><h1>Opening Mayanos analytics…</h1></section>;
  if (state.error) return (
    <section className="analytics-admin analytics-admin-state">
      <div className="analytics-lock"><Icon d="user" size={25} /></div>
      <h1>Private Mayanos analytics</h1>
      <p>{state.error}</p>
      <button className="btn" onClick={() => setRevision(value => value + 1)}>Retry</button>
      {state.signIn && <a className="btn btn-primary" href={state.signIn}>Sign in with ChatGPT</a>}
    </section>
  );

  const totals = summary?.totals || {};
  return (
    <section className="analytics-admin">
      <header className="analytics-admin-head">
        <div><span className="analytics-kicker">Private administrator view</span><h1>Mayanos analytics</h1><p>Detailed events are retained for 90 days. Daily aggregate counts remain available long-term.</p>{summary?.range && <p className="analytics-range-note">UTC calendar days: {new Date(summary.range.cutoff).toISOString().slice(0, 10)} through {new Date(summary.range.asOf).toISOString().replace("T", " ").slice(0, 16)}. Raw events and exports use this same snapshot.</p>}</div>
        <button className="btn" disabled={state.loading} onClick={() => setRevision(value => value + 1)}>{state.loading ? "Refreshing…" : "Refresh"}</button>
        <label>Reporting range<select value={days} onChange={(event) => setDays(Number(event.target.value))}>
          <option value="7">Last 7 days</option><option value="30">Last 30 days</option><option value="90">Last 90 days</option><option value="365">Last year</option>
        </select></label>
      </header>
      <SiteAppearanceSettings />
      <section className="analytics-kpis" aria-busy={state.loading}>
        <article><span>Visits</span><b>{analyticsNumber(totals.visits)}</b><small>Browser sessions started</small></article>
        <article><span>Approx. visitors</span><b>{analyticsNumber(totals.visitors)}</b><small>Distinct browsers in selected raw data (up to 90 days)</small></article>
        <article><span>Texts opened</span><b>{analyticsNumber(totals.reader_sessions)}</b><small>{analyticsNumber(totals.reading_browsers)} reading browsers · {analyticsNumber(totals.citation_successes)} confirmed source opens</small></article>
        <article><span>Signed-in accounts</span><b>{analyticsNumber(totals.accounts)}</b><small>Distinct account identifiers</small></article>
      </section>
      <p className="analytics-definitions">Visits count browser sessions started. Text opens count successfully loaded texts. Errors and background snapshots are excluded from traffic and reading charts. Source clicks recorded before the tracking update were recorded before loading; {analyticsNumber(totals.citations - totals.citation_successes)} historical clicks are not confirmed opens. Earlier text-open tracking can include preloaded texts. Section, corpus, and audience breakdowns use the last {summary?.definitions?.breakdownDays || Math.min(days, 90)} days of detailed data.</p>
      <section className="analytics-kpis analytics-source-outcomes" aria-label="Source navigation outcomes">
        <article><span>Source attempts</span><b>{analyticsNumber(summary?.sourceOutcomes?.attempts)}</b><small>Each new attempt has a unique ID</small></article>
        <article><span>Opened in reader</span><b>{analyticsNumber(summary?.sourceOutcomes?.opened)}</b><small>{analyticsNumber(summary?.sourceOutcomes?.external)} opened externally</small></article>
        <article><span>Could not open</span><b>{analyticsNumber(summary?.sourceOutcomes?.failed)}</b><small>{analyticsNumber(summary?.sourceOutcomes?.cancelled)} cancelled or left</small></article>
        <article><span>Awaiting outcome</span><b>{analyticsNumber(summary?.sourceOutcomes?.unresolved)}</b><small>In progress or no terminal event · {analyticsNumber(summary?.sourceOutcomes?.legacy_attempts)} older attempts cannot be matched by ID</small></article>
      </section>
      <section className="analytics-panels">
        <article className="analytics-panel"><header><div><span>Traffic · UTC dates</span><h2>Daily visits</h2></div></header><AnalyticsBars rows={summary?.daily || []} valueKey="visits" /></article>
        <article className="analytics-panel"><header><div><span>Reading · UTC dates</span><h2>Daily text opens</h2></div></header><AnalyticsBars rows={summary?.daily || []} valueKey="text_opens" /></article>
        <article className="analytics-panel"><header><div><span>Traffic · UTC dates</span><h2>Daily page views</h2></div></header><AnalyticsBars rows={summary?.daily || []} valueKey="page_views" /></article>
        <article className="analytics-panel"><header><div><span>Reliability · UTC dates</span><h2>Daily errors</h2></div></header><AnalyticsBars rows={summary?.daily || []} valueKey="errors" /></article>
        <article className="analytics-panel"><header><div><span>Traffic</span><h2>New vs. returning sessions</h2></div></header><AnalyticsBars rows={summary?.visitorStatus || []} /></article>
        <article className="analytics-panel"><header><div><span>Acquisition</span><h2>Referring domains</h2></div></header><AnalyticsBars rows={summary?.referrers || []} /></article>
        <article className="analytics-panel"><header><div><span>Traffic</span><h2>Entry pages</h2></div></header><AnalyticsBars rows={summary?.entryPages || []} /></article>
        <article className="analytics-panel"><header><div><span>Reliability · {analyticsNumber(totals.affected_browsers)} affected browsers</span><h2>Error trends</h2></div></header><AnalyticsBars rows={summary?.errors || []} /></article>
        <article className="analytics-panel"><header><div><span>Reliability · detailed data</span><h2>Where loading fails</h2></div></header><AnalyticsBars rows={summary?.reliability || []} showVisitors /><p className="analytics-empty">Additional-source failures are counted separately from core text loading. Retries can produce multiple errors for one browser.</p></article>
        <article className="analytics-panel"><header><div><span>Product</span><h2>Actions</h2></div></header><AnalyticsBars rows={(summary?.events || []).filter(row => !["error", "workspace_snapshot", "session_started", "page_view"].includes(row.label))} /></article>
        <article className="analytics-panel"><header><div><span>Navigation · page views</span><h2>Section visits</h2></div></header><AnalyticsBars rows={summary?.routes || []} /></article>
        <article className="analytics-panel"><header><div><span>Corpus · texts opened</span><h2>Collection activity</h2></div></header><AnalyticsBars rows={summary?.collections || []} showVisitors /></article>
        <article className="analytics-panel"><header><div><span>Corpus · texts opened</span><h2>Text activity</h2></div></header><AnalyticsBars rows={summary?.books || []} showVisitors /></article>
        <article className="analytics-panel"><header><div><span>Audience · visits</span><h2>Device activity</h2></div></header><AnalyticsBars rows={summary?.devices || []} /></article>
        <article className="analytics-panel"><header><div><span>Audience · visits</span><h2>Country activity</h2></div></header><AnalyticsBars rows={summary?.countries || []} /></article>
      </section>
      <section className="analytics-raw">
        <header>
          <div><span className="analytics-kicker">90-day detailed record</span><h2>Raw events</h2><p>Includes IP addresses, account details, searches, and workspace snapshots when present.</p></div>
          <a className="btn" href={`/bff/analytics/admin?${exportParams}`}>Export CSV</a>
        </header>
        <div className="analytics-filters">
          <label><Icon d="search" size={15} /><input aria-label="Search raw events" value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search IP, email, name, query, book, route…" /></label>
          <select aria-label="Event type" value={eventFilter} onChange={(event) => setEventFilter(event.target.value)}><option value="">All event types</option>{eventNames.map((name) => <option key={name} value={name}>{name}</option>)}</select>
          <select aria-label="Corpus category" value={collectionFilter} onChange={(event) => setCollectionFilter(event.target.value)}><option value="">All corpus categories</option>{(summary?.collections || []).map((row) => <option key={row.label} value={row.label}>{row.label}</option>)}</select>
        </div>
        {eventState.error && <p role="alert" className="analytics-request-error">{eventState.error} <button className="btn" onClick={() => loadEvents(eventState.nextCursor)}>Retry events</button></p>}
        {detailState.error && <p role="alert" className="analytics-request-error">{detailState.error}</p>}
        {detailState.loading && <p role="status">Opening event details…</p>}
        <div className="analytics-table-wrap"><table><thead><tr><th>Time</th><th>Event</th><th>Location</th><th>Book / search</th><th>Account</th><th>IP</th><th /></tr></thead>
          <tbody>{events.map((event) => <tr key={event.id}>
            <td>{new Date(Number(event.occurred_at)).toLocaleString()}</td>
            <td><b>{event.event_name}</b><small>{event.device_type} · {event.country}</small></td>
            <td>{event.route}<small>{event.path}</small></td>
            <td>{event.search_text || event.book_ref || event.collection || "—"}{event.has_workspace_snapshot ? <small>Workspace snapshot attached</small> : null}</td>
            <td>{event.account_name || "Anonymous"}<small>{event.account_email || event.visitor_id}</small></td>
            <td>{event.ip_address}</td>
            <td><button className="mini-btn" onClick={() => openEvent(event.id)}>Inspect</button></td>
          </tr>)}</tbody></table>{!events.length && !eventState.loading && !eventState.error && <p className="analytics-empty">No raw events match these filters.</p>}</div>
        <div className="analytics-pagination"><p role="status">{eventState.loading ? "Loading events…" : `${analyticsNumber(events.length)} events shown${eventState.nextCursor ? " · more available" : " · end of results"}`}</p>{eventState.nextCursor && <button className="btn" disabled={eventState.loading} onClick={() => loadEvents(eventState.nextCursor)}>Load older events</button>}</div>
      </section>
      <AnalyticsEventDetail event={selected} onClose={() => setSelected(null)} />
    </section>
  );
}

Object.assign(window, { AnalyticsAdminPage });
